import React, {useContext, useEffect, useRef, useState} from "react";
import {Dropdown} from 'primereact/dropdown';
import './RenewPage.scss';
import {InteractionRequiredAuthError} from '@azure/msal-browser';
import {DateTime} from "luxon";
import {
    DataPlantApiFactory,
    GlobalApiFactory,
    HomePlant, TimeSeriesItem,
    WindBioEnergyDetailsHistory,
    WindBioEnergyDetailsRealTime
} from "../../Services/typescript-fetch-client";
import ProfiliCaricoContext, {MainContext} from "../../Context/ProfiliCaricoContext";
import MixedPlotlyChartComponent from "../../Components/ChartComponent2/MixedPlotlyChartComponent";
import PlotsSharedLegend, {LegendSerie} from "../../Components/PlotsSharedLegend/PlotsSharedLegend";
import {ZoomElement} from "../../Models/ZoomElement";
import {ViewMode} from "./ViewMode";
import {RenewPlantType} from "./RenewPlantType";
import { useUrlStateRenew } from "./useUrlStateRenew";
import OpenNewWindow from "../../Components/OpenNewWindow/OpenNewWindow";
import CustomSidebar from "../../Components/CustomSidebar/CustomSidebar";
import {Tag} from "primereact/tag";
import {RadioButton} from "primereact/radiobutton";
import {MultiSelect} from "primereact/multiselect";
import {Button} from "primereact/button";
import {Calendar, CalendarChangeParams} from "primereact/calendar";
import { useZoom } from "../../Utils/useZoom";
import { useNavigate } from "react-router-dom";

type Props = {
    plantType: RenewPlantType
};

const RenewPage = ({ plantType }: Props) => {

    const [homePlants, setHomePlants] = useState<HomePlant[]>([]);
    const [windBioEnergyDetailsRealTimeTotal, setWindBioEnergyDetailsRealTimeTotal] = useState<WindBioEnergyDetailsRealTime[]>([]);
    const [windBioEnergyDetailsHistoryTotal, setWindBioEnergyDetailsHistoryTotal] = useState<WindBioEnergyDetailsHistory[]>([]);
    const [activeSeries, setActiveSeries] = useState<Map<string, LegendSerie>>(new Map());
    const [displayNewWindow, setDisplayNewWindow] = useState<boolean>(false);

    const { zoom, setZoom, resetZoom, zoomBack, backToInitialZoom } = useZoom();

    const context = useContext(ProfiliCaricoContext);
    let refreshTimer = useRef<any | null>();
    let containerRef = useRef<HTMLDivElement>(null);
    const { view, setView, plantName, selectedPlants, setSelectedPlants, dateSelected, setDateSelected, dateShown, isRealTime, navigateToSingles } = useUrlStateRenew(plantType, homePlants);
    const nav = useNavigate();

    useEffect(() => {
        setActiveSeries(new Map([
            ['Prodotto', {name: "Prodotto", active: true, color: "#97D700", controlledSeries: ["Prodotto", "BAD"]}],
            ['PV', {name: "PV", active: false, color: "#ea3a3e", controlledSeries: ["PV"]}],
            ['Forecast', {name: "Forecast", active: true, color: "#0f226d", controlledSeries: ["Forecast"]}],
            ['P_Nom', {name: "P_Nom", active: view === ViewMode.Single || view === ViewMode.Singles, color: "#777777", controlledSeries: ["P_Nom"], showLegend: view === ViewMode.Single || view === ViewMode.Singles}]
        ]));
    }, [view]);

    useEffect(() => {
        if (context.accessToken) {
            if(isRealTime) {
                refreshRealtimeData(progress, context, setWindBioEnergyDetailsHistoryTotal, setWindBioEnergyDetailsRealTimeTotal, view, plantType, selectedPlants, setZoom);
                refreshTimer.current = setInterval(() => {
                    refreshRealtimeData(progress, context, setWindBioEnergyDetailsHistoryTotal, setWindBioEnergyDetailsRealTimeTotal, view, plantType, selectedPlants, setZoom);
                }, 300000);//5 mins
                return () => {
                    if (refreshTimer.current != null)
                        clearInterval(refreshTimer.current);
                }
            }
            else
                refreshHistoryData(progress, context, dateShown, setWindBioEnergyDetailsHistoryTotal, setWindBioEnergyDetailsRealTimeTotal, view, plantType, selectedPlants, setZoom);
        }

        function progress(start: boolean) {
            context.setInProgressApi!(start);
        }
    }, [context.accessToken, plantType, isRealTime, selectedPlants, view, dateShown, homePlants]);

    useEffect(() => {
        let progressCount = 0;
        if (context.accessToken) {
            progress(true);
            GlobalApiFactory({accessToken: context.accessToken!, apiKey: "123"}).globalAllPlantsGet().then((homePlantsAPI: HomePlant[]) => {
                let plants = homePlantsAPI.filter(p => p.type === (plantType as HomePlant.TypeEnum));
                let plantNames: string[] = [];
                plants.forEach(p => p.plantName ? plantNames.push(p.plantName) : null);
                if (plantName && !plantNames.includes(plantName)) {
                    nav("/*")
                }
                
                plants.sort((a, b) => a.plantName!.localeCompare(b.plantName!));
                setHomePlants(plants);
                progress(false);
            }).catch((e) => {
                progress(false);
                if (e instanceof InteractionRequiredAuthError)
                    context.doAcquireTokenRedirect();
                else
                    context.showMsg("Errore", "Si è verificato un errore inaspettato nella lettura degli impianti", "error")
            });
        }

        function progress(start: boolean){
            if(start) {
                progressCount++;
                context.setInProgressApi!(true);
            }
            else{
                progressCount--;
                if(progressCount === 0)
                    context.setInProgressApi!(false);
            }
        }
    }, [context.accessToken, plantType, plantName, view]);

    useEffect(() => {
        resetZoom();
    }, [dateShown, plantName, view, selectedPlants]);

    const setSeriesStatus = (name: string, active: boolean) => {
        setActiveSeries((as: Map<string, LegendSerie>) => {
            let temp = new Map(as);
            if(temp.has(name)) {
                let el: LegendSerie = temp.get(name)!;
                temp.set(name, {
                    ...el,
                    active: active
                });
            }
            return temp;
        });
    };

    const refreshData = () => {
        if(isRealTime)
            refreshRealtimeData((start: boolean) => { context.setInProgressApi!(start); }, context, setWindBioEnergyDetailsHistoryTotal, setWindBioEnergyDetailsRealTimeTotal, view, plantType, selectedPlants, setZoom);
        else
            refreshHistoryData((start: boolean) => { context.setInProgressApi!(start); }, context, dateShown, setWindBioEnergyDetailsHistoryTotal, setWindBioEnergyDetailsRealTimeTotal, view, plantType, selectedPlants, setZoom);
    };

    const forceRealTimeMode = () => {
        const today = DateTime.now().startOf('day').toJSDate();
        setDateSelected([today, today]);
    };

    const monthNavigatorTemplate = (e: any) => {
        return <Dropdown value={e.value} options={e.options} onChange={(event) => e.onChange(event.originalEvent, event.value)} style={{ lineHeight: 1 }} />;
    }

    const yearNavigatorTemplate = (e: any) => {
        return <Dropdown value={e.value} options={e.options} onChange={(event) => e.onChange(event.originalEvent, event.value)} className="ml-2" style={{ lineHeight: 1 }} />;
    }

    const viewRealTime0 = (type: HomePlant.TypeEnum, DetailsAll: WindBioEnergyDetailsRealTime[]) => {
        return DetailsAll.map((windBioEnergyDetailsRealTime: WindBioEnergyDetailsRealTime) => {
            return <div className={(DetailsAll.length === 1 ? "col-12" : "col-6") + " graphPanel"} key={windBioEnergyDetailsRealTime.id ?? windBioEnergyDetailsRealTime.viewName}>
                <MixedPlotlyChartComponent isSingleGraph={DetailsAll.length === 1}
                                           isPlant={view === ViewMode.Single || view === ViewMode.Singles}
                                           allGraphsAction={navigateToSingles}
                                           plantName={windBioEnergyDetailsRealTime.viewName!}
                                           url={windBioEnergyDetailsRealTime.id ? "/" + type + "/" + windBioEnergyDetailsRealTime.viewName : null}
                                           showDetailKpis={[ViewMode.Total, ViewMode.Region, ViewMode.Single].includes(view)}
                                           fullScale={windBioEnergyDetailsRealTime.fullScale} type={type}
                                           visibleSeries={activeSeries}
                                           windBioEnergyDetailsRealTime={windBioEnergyDetailsRealTime}
                                           zoom={zoom!} setZoom={setZoom} zoomBack={zoomBack} backToInitialZoom={backToInitialZoom}>
                    {[ViewMode.Total, ViewMode.Single].includes(view) && <PlotsSharedLegend availableSeries={activeSeries} orientation="horizontal" serieSetVisibility={setSeriesStatus} />}
                </MixedPlotlyChartComponent>
            </div>
        })
    };

    const viewHistory0 = (type: HomePlant.TypeEnum, DetailsAll: WindBioEnergyDetailsHistory[]) => {
        return DetailsAll.map((windBioEnergyDetailsHistory: WindBioEnergyDetailsHistory) => {
            return <div className={(DetailsAll.length === 1 ? "col-12" : "col-6") + " graphPanel"} key={windBioEnergyDetailsHistory.id ?? windBioEnergyDetailsHistory.viewName}>
                <MixedPlotlyChartComponent isSingleGraph={DetailsAll.length === 1}
                                           isPlant={view === ViewMode.Single || view === ViewMode.Singles}
                                           allGraphsAction={navigateToSingles}
                                           plantName={windBioEnergyDetailsHistory.viewName!}
                                           url={windBioEnergyDetailsHistory.id ? "/" + type + "/" + windBioEnergyDetailsHistory.id : null}
                                           showDetailKpis={false}
                                           type={type} fullScale={windBioEnergyDetailsHistory.fullScale}
                                           visibleSeries={activeSeries}
                                           windBioEnergyDetailsHistory={windBioEnergyDetailsHistory}
                                           zoom={zoom!} setZoom={setZoom} zoomBack={zoomBack} backToInitialZoom={backToInitialZoom}>
                    {[ViewMode.Total, ViewMode.Single].includes(view) && <PlotsSharedLegend availableSeries={activeSeries} orientation="horizontal" serieSetVisibility={setSeriesStatus} />}
                </MixedPlotlyChartComponent>
            </div>
        });
    };
    
    return <div className="m-1 with-footer" ref={containerRef}>
        {view === ViewMode.Singles && <OpenNewWindow type={plantType} displayNewWindow={displayNewWindow} setDisplayNewWindow={setDisplayNewWindow} plants={homePlants} unselectPlants={selectedPlants} />}
        <CustomSidebar refreshAction={refreshData} container={containerRef}>
            <>
                {(plantType === HomePlant.TypeEnum.Wind) && <h3>Impostazioni Eolico</h3>}
                {(plantType === HomePlant.TypeEnum.Bioenergy) && <h3>Impostazioni Bioenergie</h3>}
                {(plantType === HomePlant.TypeEnum.Solar) && <h3>Impostazioni Fotovoltaico</h3>}
                <div className="tagState">
                    <Tag severity={!isRealTime ? "warning": "success"} rounded >{isRealTime ? "Modalità Realtime" : "Modalità Storico"}</Tag>
                </div>
                <div className="card SorgeniaHomeReport">
                    {view !== ViewMode.Single && <>
                        <div className="flex field-radiobutton sorgeniaWindRadiobutton">
                            <RadioButton inputId="view0" name="view" value={ViewMode.Total} onChange={(e) => {setView(ViewMode.Total)}} checked={view === ViewMode.Total} />
                            <label htmlFor="view0">Totale</label>
                        </div>
                        <div className="flex field-radiobutton sorgeniaWindRadiobutton">
                            <RadioButton
                                inputId="view1" name="view" value={ViewMode.Region} onChange={(e) => {setView(ViewMode.Region)}} checked={view === ViewMode.Region} />
                            <label htmlFor="view1">Zone</label>
                        </div>
                        <div className="flex field-radiobutton sorgeniaWindRadiobutton">
                            <RadioButton inputId="view2" name="view" value={ViewMode.Singles} onChange={(e) => {setSelectedPlants(homePlants); setView(ViewMode.Singles)}} checked={view === ViewMode.Singles} />
                            <label htmlFor="view2">Unità</label>
                        </div>
                        {(view === ViewMode.Singles) &&
                            <div className="plantSelector flex flex-row align-items-start justify-content-end">
                                <MultiSelect value={selectedPlants} options={homePlants} onChange={(e) => {setSelectedPlants(e.value);}}
                                             optionLabel="plantName" placeholder="Visualizza Impianti" maxSelectedLabels={1} selectedItemsLabel="{0} impianti selezionati"/>
                                <Button onClick={() => {context.setSidebarIsOpen!(false); setDisplayNewWindow(true)}}
                                        icon="pi pi-external-link" title="Apri impianti in nuova finestra" className="ml-1" />
                            </div>}
                    </>}

                    <div className="mb-2 mt-2 history-picker-container">
                        <Calendar placeholder="Date range" className="history-picker" value={dateSelected as Date | Date[]} onChange={(e: CalendarChangeParams) => { setDateSelected(e.value); }} monthNavigator yearNavigator yearRange="2010:2030"
                                  monthNavigatorTemplate={monthNavigatorTemplate} yearNavigatorTemplate={yearNavigatorTemplate} showIcon selectionMode="range" readOnlyInput locale="it" />
                    </div>
                    <div className="flex justify-content-end">
                        <div className="flex align-items-start">
                            <Button label="Realtime" icon="pi pi-clock" onClick={forceRealTimeMode } />
                        </div>
                    </div>
                </div>
                <PlotsSharedLegend availableSeries={activeSeries} title="Dati Visualizzati" orientation="vertical" serieSetVisibility={setSeriesStatus} />
            </>
        </CustomSidebar>
            <div className="grid no-padding">
                {isRealTime ? viewRealTime0((plantType), windBioEnergyDetailsRealTimeTotal) :
                    viewHistory0((plantType), windBioEnergyDetailsHistoryTotal)}
            </div>
        {[ViewMode.Singles, ViewMode.Region].includes(view) && <PlotsSharedLegend availableSeries={activeSeries} orientation="horizontal" serieSetVisibility={setSeriesStatus} />}
    </div>;
}

function refreshRealtimeData(progress: (start: boolean) => void, context: MainContext,
                             setWindBioEnergyDetailsHistoryTotal: React.Dispatch<React.SetStateAction<WindBioEnergyDetailsHistory[]>>,
                             setWindBioEnergyDetailsRealTimeTotal: React.Dispatch<React.SetStateAction<WindBioEnergyDetailsRealTime[]>>,
                             view: ViewMode,
                             plantType: RenewPlantType,
                             selectedPlants: HomePlant[],
                             setZoom: React.Dispatch<React.SetStateAction<ZoomElement | null>>) {
    setWindBioEnergyDetailsHistoryTotal([]);
    setWindBioEnergyDetailsRealTimeTotal([]);
    if (context.isAuthenticated) {
        switch (view) {
            case ViewMode.Total:
                progress(true);
                DataPlantApiFactory({accessToken: context.accessToken!, apiKey: "123"}).plantsPlantTypeTotalGet(plantType).then((windBioEnergyDetailsRealTimeAPI: WindBioEnergyDetailsRealTime[]) => {
                    windBioEnergyDetailsRealTimeAPI.sort((a, b) => { return a.order === b.order ? a.viewName!.localeCompare(b.viewName!) : a.order > b.order ? 1 : -1; });
                    setWindBioEnergyDetailsRealTimeTotal(windBioEnergyDetailsRealTimeAPI);

                    let allDates = ([] as TimeSeriesItem[]).concat(windBioEnergyDetailsRealTimeAPI[0].series!.pv ?? [],
                        windBioEnergyDetailsRealTimeAPI[0].series!.forecast ?? [], windBioEnergyDetailsRealTimeAPI[0].series!.nominalPower ?? [], windBioEnergyDetailsRealTimeAPI[0].series!.prodotto ?? [])
                        .map(v => DateTime.fromISO(v.dateKey! as unknown as string));
                    setZoom({
                        x: [DateTime.min(...allDates),
                            DateTime.max(...allDates)],
                        y: [0, windBioEnergyDetailsRealTimeAPI[0].fullScale]
                    });

                    progress(false);
                }).catch((e) => {
                    progress(false);
                    if (e instanceof InteractionRequiredAuthError)
                        context.doAcquireTokenRedirect();
                });
                break;
            case ViewMode.Region:
                progress(true);
                DataPlantApiFactory({accessToken: context.accessToken!, apiKey: "123"}).plantsPlantTypeByRegionAllGet(plantType).then((windBioEnergyDetailsRealTimeAPI: WindBioEnergyDetailsRealTime[]) => {
                    windBioEnergyDetailsRealTimeAPI.sort((a, b) => { return a.order === b.order ? a.viewName!.localeCompare(b.viewName!) : a.order > b.order ? 1 : -1; });
                    setWindBioEnergyDetailsRealTimeTotal(windBioEnergyDetailsRealTimeAPI);

                    let allDates = ([] as TimeSeriesItem[]).concat(windBioEnergyDetailsRealTimeAPI[0].series!.pv ?? [],
                        windBioEnergyDetailsRealTimeAPI[0].series!.forecast ?? [], windBioEnergyDetailsRealTimeAPI[0].series!.nominalPower ?? [], windBioEnergyDetailsRealTimeAPI[0].series!.prodotto ?? [])
                        .map(v => DateTime.fromISO(v.dateKey! as unknown as string));
                    setZoom({
                        x: [DateTime.min(...allDates),
                            DateTime.max(...allDates)],
                        y: [0, windBioEnergyDetailsRealTimeAPI[0].fullScale]
                    });
                    progress(false);
                }).catch((e) => {
                    progress(false);
                    if (e instanceof InteractionRequiredAuthError)
                        context.doAcquireTokenRedirect();
                });
                break;
            case ViewMode.Single:
            case ViewMode.Singles:
                let promises : Promise<WindBioEnergyDetailsRealTime[]>[] = [];
                if (selectedPlants != null) {
                    for (let i = 0; i < selectedPlants.length; i++) {
                        let homePlant: HomePlant = selectedPlants[i];
                        progress(true);
                        promises.push(DataPlantApiFactory({
                            accessToken: context.accessToken!,
                            apiKey: "123"
                        }).plantsPlantTypeByNameGet(plantType, homePlant.plantName!))
                    }
                    Promise.all(promises).then((values) => {
                        let temp : WindBioEnergyDetailsRealTime[] = values.map(v => v[0]);
                        temp.sort((a, b) => { return a.order === b.order ? a.viewName!.localeCompare(b.viewName!) : a.order > b.order ? 1 : -1; });
                        setWindBioEnergyDetailsRealTimeTotal(temp);

                        let allDates = ([] as TimeSeriesItem[]).concat(temp[0].series!.pv ?? [],
                            temp[0].series!.forecast ?? [], temp[0].series!.nominalPower ?? [], temp[0].series!.prodotto ?? [])
                            .map(v => DateTime.fromISO(v.dateKey! as unknown as string));
                        setZoom({
                            x: [DateTime.min(...allDates),
                                DateTime.max(...allDates)],
                            y: [0, temp[0].fullScale]
                        });

                        progress(false);
                    }).catch((e) => {
                        progress(false);
                        if (e instanceof InteractionRequiredAuthError)
                            context.doAcquireTokenRedirect();
                    });
                }
                break;
            default:
                break;
        }
    }
}

function refreshHistoryData(progress: (start: boolean) => void, context: MainContext, dateShown: DateTime[],
                            setWindBioEnergyDetailsHistoryTotal: React.Dispatch<React.SetStateAction<WindBioEnergyDetailsHistory[]>>,
                            setWindBioEnergyDetailsRealTimeTotal: React.Dispatch<React.SetStateAction<WindBioEnergyDetailsRealTime[]>>,
                            view: ViewMode, plantType: RenewPlantType, selectedPlants: HomePlant[],
                            setZoom: React.Dispatch<React.SetStateAction<ZoomElement | null>>) {
    if (dateShown[0] != null && dateShown[1] != null) {
        setWindBioEnergyDetailsRealTimeTotal([]);
        setWindBioEnergyDetailsHistoryTotal([]);
        if (context.isAuthenticated) {
            switch (view) {
                case ViewMode.Total:
                    progress(true);
                    DataPlantApiFactory({
                        accessToken: context.accessToken!,
                        apiKey: "123"
                    }).plantsPlantTypeTotalHistoryGet(plantType, dateShown[0].toISODate(), dateShown[1].toISODate()).then((windBioEnergyDetailsHistoryAPI: WindBioEnergyDetailsHistory[]) => {
                        windBioEnergyDetailsHistoryAPI.sort((a, b) => { return a.order === b.order ? a.viewName!.localeCompare(b.viewName!) : a.order > b.order ? 1 : -1; });
                        setWindBioEnergyDetailsHistoryTotal(windBioEnergyDetailsHistoryAPI);

                        let allDates = ([] as TimeSeriesItem[]).concat(windBioEnergyDetailsHistoryAPI[0].series!.pv ?? [],
                            windBioEnergyDetailsHistoryAPI[0].series!.forecast ?? [], windBioEnergyDetailsHistoryAPI[0].series!.nominalPower ?? [], windBioEnergyDetailsHistoryAPI[0].series!.prodotto ?? [])
                            .map(v => DateTime.fromISO(v.dateKey! as unknown as string));
                        setZoom({
                            x: [DateTime.min(...allDates),
                                DateTime.max(...allDates)],
                            y: [0, windBioEnergyDetailsHistoryAPI[0].fullScale]
                        });
                        progress(false);
                    }).catch((e) => {
                        progress(false);
                        if (e instanceof InteractionRequiredAuthError)
                            context.doAcquireTokenRedirect();
                    });
                    break;
                case ViewMode.Region:
                    progress(true);
                    DataPlantApiFactory({
                        accessToken: context.accessToken!,
                        apiKey: "123"
                    }).plantsPlantTypeByRegionAllHistoryGet(plantType, dateShown[0].toISODate(), dateShown[1].toISODate()).then((windBioEnergyDetailsHistoryAPI: WindBioEnergyDetailsHistory[]) => {
                        windBioEnergyDetailsHistoryAPI.sort((a, b) => { return a.order === b.order ? a.viewName!.localeCompare(b.viewName!) : a.order > b.order ? 1 : -1; });
                        setWindBioEnergyDetailsHistoryTotal(windBioEnergyDetailsHistoryAPI);

                        let allDates = ([] as TimeSeriesItem[]).concat(windBioEnergyDetailsHistoryAPI[0].series!.pv ?? [],
                            windBioEnergyDetailsHistoryAPI[0].series!.forecast ?? [], windBioEnergyDetailsHistoryAPI[0].series!.nominalPower ?? [], windBioEnergyDetailsHistoryAPI[0].series!.prodotto ?? [])
                            .map(v => DateTime.fromISO(v.dateKey! as unknown as string));
                        setZoom({
                            x: [DateTime.min(...allDates),
                                DateTime.max(...allDates)],
                            y: [0, windBioEnergyDetailsHistoryAPI[0].fullScale]
                        });
                        progress(false);
                    }).catch((e) => {
                        progress(false);
                        if (e instanceof InteractionRequiredAuthError)
                            context.doAcquireTokenRedirect();
                    });
                    break;
                case ViewMode.Single:
                case ViewMode.Singles:
                    setWindBioEnergyDetailsHistoryTotal([]);
                    if (selectedPlants != null) {
                        let promises: Promise<WindBioEnergyDetailsHistory[]>[] = [];
                        for (let i = 0; i < selectedPlants.length; i++) {
                            let homePlant: HomePlant = selectedPlants[i];
                            progress(true);
                            promises.push(DataPlantApiFactory({
                                accessToken: context.accessToken!,
                                apiKey: "123"
                            }).plantsPlantTypeByIdHistoryGet(plantType, homePlant.id!, dateShown[0].toISODate(), dateShown[1].toISODate()));
                        }
                        Promise.all(promises).then((values) => {
                            let temp : WindBioEnergyDetailsRealTime[] = values.map(v => v[0]);
                            temp.sort((a, b) => { return a.order === b.order ? a.viewName!.localeCompare(b.viewName!) : a.order > b.order ? 1  : -1; });
                            setWindBioEnergyDetailsHistoryTotal(temp);

                            let allDates = ([] as TimeSeriesItem[]).concat(temp[0].series!.pv ?? [],
                                temp[0].series!.forecast ?? [], temp[0].series!.nominalPower ?? [], temp[0].series!.prodotto ?? [])
                                .map(v => DateTime.fromISO(v.dateKey! as unknown as string));

                            setZoom({
                                x: [DateTime.min(...allDates),
                                    DateTime.max(...allDates)],
                                y: [0, temp[0].fullScale]
                            });
                            progress(false);
                        }).catch((e) => {
                            progress(false);
                            if (e instanceof InteractionRequiredAuthError)
                                context.doAcquireTokenRedirect();
                        });
                    }
                    break;
                default:
                    break;
            }
        }
    }
}

export default RenewPage;